import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { differenceInMinutes, isAfter, isBefore, parseISO } from 'date-fns';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { ErrorState } from '@/shared/ui/ErrorState';
import { Icon } from '@/shared/ui/Icon';
import { getMeeting, listParticipants } from '@/shared/api/meetings';
import { listPlaceCandidates } from '@/shared/api/voting';
import { MeetingTabs } from '@/features/meeting/MeetingTabs';
import { CharSpringMorph } from '@/shared/ui/CharSpringMorph';
import { listScheduleItems, type ScheduleItem } from '@/shared/api/schedule';
import { fmtKst } from '@/shared/lib/date';

// 모임 당일 라이브 대시보드 — 읽기 전용 + 빠른 액션 진입.
// 30초마다 현재 시각을 갱신해 진행 중 일정을 강조한다.
const CLOCK_TICK_MS = 30_000;

type ItemPhase = 'done' | 'now' | 'upcoming';

function phaseOf(item: ScheduleItem, now: Date): ItemPhase {
  const start = parseISO(item.startAt);
  const end = item.endAt ? parseISO(item.endAt) : null;
  if (end ? isAfter(now, end) : false) return 'done';
  if (!end && isAfter(now, start)) {
    // 종료 시간이 없으면 시작 후 2시간까지 '진행 중'으로 본다
    return differenceInMinutes(now, start) <= 120 ? 'now' : 'done';
  }
  if (end && isAfter(now, start) && isBefore(now, end)) return 'now';
  return 'upcoming';
}

function countdownLabel(target: Date, now: Date): string {
  const mins = differenceInMinutes(target, now);
  if (mins <= 0) return '지금';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h >= 24) return `${Math.floor(h / 24)}일 ${h % 24}시간 후`;
  if (h > 0) return `${h}시간 ${m}분 후`;
  return `${m}분 후`;
}

function kakaoDirectionUrl(name: string, x?: string | null, y?: string | null) {
  if (!x || !y) return null;
  return `https://map.kakao.com/link/to/${encodeURIComponent(name)},${y},${x}`;
}


export default function LivePage() {
  const { id = '' } = useParams<{ id: string }>();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const meetingQ = useQuery({
    queryKey: ['meeting', id],
    queryFn: () => getMeeting(id),
    enabled: !!id,
  });
  const partsQ = useQuery({
    queryKey: ['participants', id],
    queryFn: () => listParticipants(id),
    enabled: !!id,
  });
  const scheduleQ = useQuery({
    queryKey: ['schedule', id],
    queryFn: () => listScheduleItems(id),
    enabled: !!id,
    refetchInterval: CLOCK_TICK_MS,
  });
  const placesQ = useQuery({
    queryKey: ['places', id],
    queryFn: () => listPlaceCandidates(id),
    enabled: !!id,
    refetchInterval: CLOCK_TICK_MS,
  });

  if (meetingQ.isLoading) return <SkeletonCard />;
  if (meetingQ.isError || !meetingQ.data) {
    return <ErrorState title="모임을 불러오지 못했어요" onRetry={() => meetingQ.refetch()} />;
  }

  const m = meetingQ.data;

  if (!m.confirmedDate) {
    return (
      <section className="space-y-5">
        <MeetingTabs meetingId={id} />
        <div className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-8 text-center">
          <p className="text-section font-semibold text-ink">날짜 미확정</p>
          <p className="mt-2 text-body text-ink-muted">날짜가 확정되면 라이브 화면이 열려요.</p>
        </div>
      </section>
    );
  }

  const startAt = m.confirmedDate ? parseISO(m.confirmedDate) : null;
  const started = startAt ? !isBefore(now, startAt) : false;
  const items = [...(scheduleQ.data ?? [])].sort(
    (a, b) => a.startAt.localeCompare(b.startAt),
  );
  const nowItem = items.find((it) => phaseOf(it, now) === 'now');
  const nextItem = items.find((it) => phaseOf(it, now) === 'upcoming');
  const currentItem = nowItem ?? nextItem ?? items[0] ?? null;
  const placesById = new Map((placesQ.data ?? []).map((place) => [place.id, place]));
  const currentPlace = currentItem?.placeCandidateId ? placesById.get(currentItem.placeCandidateId) : null;
  const nextPlace = nextItem?.placeCandidateId ? placesById.get(nextItem.placeCandidateId) : null;
  const currentDirectionsUrl = currentPlace
    ? (kakaoDirectionUrl(currentPlace.name, currentPlace.x, currentPlace.y) ?? currentPlace.url)
    : kakaoDirectionUrl(m.meetingPointName ?? m.region ?? m.title, m.meetingPointX, m.meetingPointY);
  const participantNames = (partsQ.data ?? []).filter((p) => !p.leftAt).map((p) => p.nickname);
  const activeCount = participantNames.length;
  const nextStart = nextItem ? parseISO(nextItem.startAt) : null;
  const minutesLeft = nowItem?.endAt
    ? Math.max(0, differenceInMinutes(parseISO(nowItem.endAt), now))
    : nextStart
      ? Math.max(0, differenceInMinutes(nextStart, now))
      : 0;
  const heroTitle = currentItem?.title ?? m.title;
  const heroSub = started
    ? `${nowItem ? '진행 중' : '대기 중'} · 다음 이동까지 ${minutesLeft}분`
    : startAt
      ? `시작까지 ${countdownLabel(startAt, now)}`
      : `${participantNames.length}명 함께`;
  const people = participantNames.slice(0, 3);

  return (
    <section className="-mx-4 -my-4 flex min-h-[calc(100vh-64px)] flex-col bg-canvas">
      <div className="sticky top-0 z-10 bg-surface">
        <div className="flex items-center gap-2 border-b border-surface-border px-3.5 py-2.5">
          <Link
            to={`/meetings/${id}`}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] bg-canvas text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
            aria-label="모임으로 돌아가기"
          >
            <Icon name="back" size={18} />
          </Link>
          <p className="min-w-0 flex-1 truncate text-[14px] font-semibold tracking-[-0.02em] text-ink">
            {m.title}
          </p>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-2 py-1 text-[10px] font-semibold text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
            LIVE
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-3">
        <div className="mx-3.5 mt-3 space-y-3">
          <MeetingTabs meetingId={id} />
        </div>
        <div className="relative mx-3.5 mt-2 overflow-hidden rounded-[18px] bg-hero p-4 text-white shadow-[var(--hero-glow)]">
          <div
            className="absolute -right-9 -top-9 h-[120px] w-[120px] rounded-full bg-white/12"
            aria-hidden="true"
          />
          <p className="text-[10px] font-semibold tracking-[0.12em] text-primary-light">
            {started ? 'NOW PLAYING' : 'TODAY'}
          </p>
          <h1 className="mt-2 text-[21px] font-semibold leading-[1.2] tracking-[-0.04em]">
            {heroTitle}
          </h1>
          <div className="mt-1.5 text-[12px] text-white/72">
            {!started && startAt ? (
              <div className="flex items-center gap-1">
                시작까지
                <CharSpringMorph value={countdownLabel(startAt, now)} gap={0} className="inline-flex" />
              </div>
            ) : (
              heroSub
            )}
          </div>
          <div className="mt-3.5 flex gap-2">
            <div className="flex-1 rounded-xl bg-white/10 p-2.5">
              <p className="text-[10px] font-medium text-white/55">참여</p>
              <p className="mt-1 text-[16px] font-semibold">
                {activeCount}명
              </p>
            </div>
            <div className="flex-1 rounded-xl bg-white/10 p-2.5">
              <p className="text-[10px] font-medium text-white/55">다음</p>
              <p className="mt-1 text-[16px] font-semibold">
                {nextItem ? fmtKst(nextItem.startAt, 'HH:mm') : '완료'}
              </p>
            </div>
          </div>
        </div>

        <div className="mx-3.5 mt-3 flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-ink">현재 장소</h2>
          <Link
            to={`/meetings/${id}/chat`}
            className="rounded-[7px] bg-primary-soft px-2 py-1 text-[11px] font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
          >
            채팅
          </Link>
        </div>

        <div className="mx-3.5 mt-2 overflow-hidden rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm">
          <div className="relative h-[88px] bg-gradient-to-br from-primary-soft to-success-soft">
            <div
              className="absolute left-1/2 top-6 h-[34px] w-[34px] -translate-x-1/2 rotate-[-45deg] rounded-[50%_50%_50%_4px] bg-primary shadow-[0_8px_18px_rgba(120,201,87,0.25)] after:absolute after:inset-2.5 after:rounded-full after:bg-surface"
              aria-hidden="true"
            />
          </div>
          <div className="p-3.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="mb-1.5 inline-flex rounded-full bg-primary-soft px-2 py-1 text-[10px] font-semibold text-primary">
                  {nowItem ? '진행 중' : started ? '대기 중' : '시작 전'}
                </span>
                <p className="truncate text-[17px] font-semibold tracking-[-0.03em] text-ink">
                  {currentPlace?.name ?? currentItem?.title ?? m.title}
                </p>
                <p className="mt-1 text-[11px] text-ink-muted">
                  {currentPlace?.address ?? m.meetingPointName ?? m.region ?? '장소 미정'}
                  {currentItem ? ` · ${fmtKst(currentItem.startAt, 'HH:mm')}` : ''}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-[20px] font-semibold tracking-[-0.04em] text-ink">
                  {minutesLeft}
                </p>
                <p className="mt-0.5 text-[10px] text-ink-disabled">분 남음</p>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Link
                to={`/meetings/${id}/chat`}
                className="flex-1 min-h-11 rounded-[11px] bg-primary px-2 py-2.5 text-center text-[12px] font-semibold text-white focus-visible:outline-none focus-visible:shadow-focus-ring"
              >
                상태 공유
              </Link>
              {currentDirectionsUrl ? (
                <a
                  href={currentDirectionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 min-h-11 rounded-[11px] bg-canvas px-2 py-2.5 text-center text-[12px] font-semibold text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
                >
                  길찾기
                </a>
              ) : (
                <Link
                  to={`/meetings/${id}/places?from=live&mode=map`}
                  className="flex-1 min-h-11 rounded-[11px] bg-canvas px-2 py-2.5 text-center text-[12px] font-semibold text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
                >
                  장소 보기
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="mx-3.5 mt-3 flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-ink">다음 코스</h2>
        </div>
        <div className="mx-3.5 mt-2 rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-3.5">
          {scheduleQ.isError || placesQ.isError ? (
            <ErrorState
              title="코스를 불러오지 못했어요"
              onRetry={() => {
                scheduleQ.refetch();
                placesQ.refetch();
              }}
            />
          ) : nextItem ? (
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-warning-soft text-warning">
                <Icon name="pin" size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-semibold text-ink">{nextPlace?.name ?? nextItem.title}</p>
                <p className="mt-1 text-[11px] text-ink-disabled">
                  {fmtKst(nextItem.startAt, 'HH:mm')}
                  {nextItem.memo ? ` · ${nextItem.memo}` : ''}
                </p>
              </div>
              <span className="rounded-full bg-warning-soft px-2 py-1 text-[10px] font-semibold text-warning">
                다음
              </span>
            </div>
          ) : (
            <p className="text-[12px] font-semibold text-ink-muted">남은 코스가 없어요.</p>
          )}
        </div>

        <div className="mx-3.5 mt-3 flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-ink">참여자 상태</h2>
          <span className="rounded-[7px] bg-primary-soft px-2 py-1 text-[11px] font-semibold text-primary">
            전체
          </span>
        </div>
        <div className="mx-3.5 mt-2 overflow-hidden rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm">
          {people.length === 0 ? (
            <p className="p-3.5 text-[12px] font-semibold text-ink-muted">참여자 정보를 불러오는 중이에요.</p>
          ) : (
            people.map((name, index) => {
              const status = started ? '참여 중' : '대기';
              const statusClass = started ? 'bg-primary-soft text-primary' : 'bg-surface-muted text-ink-muted';
              return (
                <div
                  key={`${name}-${index}`}
                  className="flex items-center gap-2.5 border-t border-surface-border px-3.5 py-3 first:border-t-0"
                >
                  <span
                    className={`flex h-[34px] w-[34px] items-center justify-center rounded-xl text-[12px] font-semibold text-white ${
                      index === 0 ? 'bg-primary' : index === 1 ? 'bg-success' : 'bg-warning'
                    }`}
                  >
                    {name.slice(0, 1)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-ink">{name}</p>
                    <p className="mt-0.5 text-[10.5px] text-ink-disabled">
                      도착 상태는 채팅에서 공유해요
                    </p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${statusClass}`}>
                    {status}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div className="mx-3.5 mt-3 flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-ink">코스 진행</h2>
        </div>
        <div className="mx-3.5 mt-2 rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-3.5">
          {scheduleQ.isLoading ? (
            <p className="text-[12px] font-semibold text-ink-muted">불러오는 중…</p>
          ) : items.length === 0 ? (
            <p className="text-[12px] font-semibold text-ink-muted">등록된 일정이 없어요.</p>
          ) : (
            <ol aria-label="당일 일정">
              {items.map((item, index) => {
                const phase = phaseOf(item, now);
                return (
                  <li key={item.id} className="flex gap-2.5 pb-3 last:pb-0">
                    <div className="flex w-2.5 flex-col items-center">
                      <span
                        className={`mt-1 h-[9px] w-[9px] rounded-full ${
                          phase === 'done'
                            ? 'bg-success'
                            : phase === 'now'
                              ? 'bg-primary'
                              : 'bg-warning'
                        }`}
                        aria-hidden="true"
                      />
                      {index < items.length - 1 && (
                        <span className="mt-1 w-px flex-1 bg-surface-border" aria-hidden="true" />
                      )}
                    </div>
                    <time className="w-8 shrink-0 pt-0.5 text-right text-[10px] font-semibold text-ink-disabled">
                      {fmtKst(item.startAt, 'HH:mm')}
                    </time>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold text-ink">{item.title}</p>
                      <p className="mt-0.5 text-[10.5px] text-ink-disabled">
                        {phase === 'done' ? '완료' : phase === 'now' ? '진행 중' : '다음'}
                        {item.memo ? ` · ${item.memo}` : ''}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="mx-3.5 mt-3 flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-ink">빠른 액션</h2>
        </div>
        <div className="mx-3.5 mt-2 space-y-2">
          {[
            { to: `/meetings/${id}/chat`, icon: 'chat' as const, title: '단체 채팅 열기', meta: '도착 여부와 이동 시간을 공유해요' },
            { to: `/meetings/${id}/expense`, icon: 'coin' as const, title: '지출 추가', meta: '오늘 쓴 비용을 바로 기록해요' },
          ].map((action) => (
            <Link
              key={action.to}
              to={action.to}
              className="flex items-center gap-2.5 rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-3.5 focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-soft text-primary">
                <Icon name={action.icon} size={18} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14px] font-semibold text-ink">{action.title}</span>
                <span className="mt-1 block truncate text-[11px] text-ink-disabled">
                  {action.meta}
                </span>
              </span>
              <span className="rounded-full bg-primary-soft px-2 py-1 text-[10px] font-semibold text-primary">
                열기
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
